/**
 * Shared manual entry form fields for Guardian/Principal forms.
 * Reduces cognitive complexity by extracting repeated field patterns.
 */
import React from "react";
import PropTypes from "prop-types";
import { TextField, MenuItem, Box, Typography, InputAdornment, CircularProgress } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

export function getEmailRequiredMessage(isNewPg, isEsignatures) {
  if (isNewPg && isEsignatures) {
    return "Email is required for new professional guardians and for e-signature processing of indemnity agreements.";
  }
  if (isNewPg) {
    return "Email is required for new professional guardians to create their account in the system.";
  }
  if (isEsignatures) {
    return "Email is required for e-signature processing of indemnity agreements.";
  }
  return "Email is required.";
}

/**
 * Email Required Info Box
 */
export const EmailRequiredInfo = ({ isNewPg, isEsignatures }) => {
  const message = getEmailRequiredMessage(isNewPg, isEsignatures);

  return (
    <Box sx={{ mb: 2, p: 2, backgroundColor: "#f5f5f5", borderRadius: 1, border: "1px solid #e0e0e0" }}>
      <Typography variant="body2" color="text.secondary">
        <strong>Email Required:</strong> {message}
      </Typography>
    </Box>
  );
};

EmailRequiredInfo.propTypes = {
  isNewPg: PropTypes.bool,
  isEsignatures: PropTypes.bool,
};

/**
 * Citizenship dropdown with "Other" custom input
 */
export const CitizenshipField = ({
  value,
  customValue,
  onChange,
  onCustomChange,
  disabled,
  fieldKey = "citizenship",
  citizenshipSelectSx,
  specifyCitizenshipSx,
}) => (
  <Box>
    <TextField
      select
      sx={citizenshipSelectSx}
      label="Citizenship"
      disabled={disabled}
      value={value === "Other" ? "Other" : value || "USA"}
      onChange={(e) => onChange(e.target.value)}
      fullWidth
      required
    >
      <MenuItem value="USA">USA</MenuItem>
      <MenuItem value="Canada">Canada</MenuItem>
      <MenuItem value="UK">UK</MenuItem>
      <MenuItem value="Mexico">Mexico</MenuItem>
      <MenuItem value="Germany">Germany</MenuItem>
      <MenuItem value="France">France</MenuItem>
      <MenuItem value="Australia">Australia</MenuItem>
      <MenuItem value="India">India</MenuItem>
      <MenuItem value="Pakistan">Pakistan</MenuItem>
      <MenuItem value="China">China</MenuItem>
      <MenuItem value="Other">Other (Please specify)</MenuItem>
    </TextField>

    {value === "Other" && (
      <TextField
        sx={specifyCitizenshipSx}
        label="Specify Citizenship"
        disabled={disabled}
        value={customValue || ""}
        onChange={(e) => onCustomChange(e.target.value)}
        fullWidth
        required
        placeholder="Enter citizenship"
      />
    )}
  </Box>
);

CitizenshipField.propTypes = {
  value: PropTypes.string,
  customValue: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onCustomChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  fieldKey: PropTypes.string,
  citizenshipSelectSx: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  specifyCitizenshipSx: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
};

/**
 * Country dropdown with "Other" custom input
 */
export const CountryField = ({
  value,
  customValue,
  onChange,
  onCustomChange,
  disabled,
  countrySelectSx,
  specifyCountrySx,
}) => (
  <Box>
    <TextField
      select
      sx={countrySelectSx}
      label="Country of Residence"
      disabled={disabled}
      value={value === "Other" ? "Other" : value || "USA"}
      onChange={(e) => onChange(e.target.value)}
      fullWidth
      required
    >
      <MenuItem value="USA">USA</MenuItem>
      <MenuItem value="Canada">Canada</MenuItem>
      <MenuItem value="UK">UK</MenuItem>
      <MenuItem value="Mexico">Mexico</MenuItem>
      <MenuItem value="Germany">Germany</MenuItem>
      <MenuItem value="France">France</MenuItem>
      <MenuItem value="Australia">Australia</MenuItem>
      <MenuItem value="India">India</MenuItem>
      <MenuItem value="Pakistan">Pakistan</MenuItem>
      <MenuItem value="China">China</MenuItem>
      <MenuItem value="Other">Other (Please specify)</MenuItem>
    </TextField>

    {value === "Other" && (
      <TextField
        sx={specifyCountrySx}
        label="Specify Country"
        disabled={disabled}
        value={customValue || ""}
        onChange={(e) => onCustomChange(e.target.value)}
        fullWidth
        required
        placeholder="Enter country"
      />
    )}
  </Box>
);

CountryField.propTypes = {
  value: PropTypes.string,
  customValue: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onCustomChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  countrySelectSx: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  specifyCountrySx: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
};

/**
 * SSN field with visibility toggle
 */
export const SSNField = ({ value, showSSN, onToggleVisibility, onChange, disabled, hasPiiAccess }) => {
  const formatSSN = (ssn) => {
    if (!ssn || ssn.length < 4) return ssn || "";
    return "XXX-XX-" + ssn.slice(-4);
  };

  return (
    <TextField
      label="Social Security Number (SSN)"
      type={hasPiiAccess && showSSN ? "text" : "password"}
      disabled={disabled}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      fullWidth
      required
      helperText={formatSSN(value || "")}
      slotProps={{ input: {
        endAdornment: hasPiiAccess ? (
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle ssn visibility"
              onClick={onToggleVisibility}
              edge="end"
              disabled={disabled}
            >
              {showSSN ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ) : null,
      }}}
    />
  );
};

SSNField.propTypes = {
  value: PropTypes.string,
  showSSN: PropTypes.bool,
  onToggleVisibility: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  hasPiiAccess: PropTypes.bool,
};

/**
 * Employment Status and Occupation fields
 */
export const EmploymentOccupationFields = ({
  employmentStatus,
  occupationText,
  questionLabel,
  onChange,
  disabled,
}) => (
  <>
    <TextField
      select
      label="Employment Status"
      value={employmentStatus || ""}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value, occupationText)}
      fullWidth
      required
    >
      <MenuItem value="Employed">Employed</MenuItem>
      <MenuItem value="Unemployed">Unemployed</MenuItem>
      <MenuItem value="Retired">Retired</MenuItem>
    </TextField>

    {(employmentStatus === "Employed" || employmentStatus === "Retired") && (
      <TextField
        label={questionLabel}
        value={occupationText || ""}
        disabled={disabled}
        onChange={(e) => onChange(employmentStatus, e.target.value)}
        fullWidth
        required
      />
    )}
  </>
);

EmploymentOccupationFields.propTypes = {
  employmentStatus: PropTypes.string,
  occupationText: PropTypes.string,
  questionLabel: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

/**
 * Address fields with Google Autocomplete, ZIP, City, State
 */
export const AddressFields = ({
  address,
  zipCode,
  city,
  state,
  onAddressChange,
  onZipChange,
  onCityChange,
  onStateChange,
  onGoogleCallback,
  disabled,
  loading,
  allCities,
  allStates,
  GoogleAutoComplete,
  LabeledCreatableSelect,
  googlePlaceholder = "Search Address",
  streetTextFieldProps = {},
  zipTextFieldProps = {},
}) => (
  <>
    <Box>
      <Typography
        sx={{
          fontSize: "12px",
          color: "rgba(0, 0, 0, 0.6)",
          marginBottom: "4px",
          fontWeight: 500,
        }}
      >
        Search Address
      </Typography>
      <GoogleAutoComplete placeholder={googlePlaceholder} callback={onGoogleCallback} />
    </Box>

    <TextField
      sx={{ mt: 1, mb: 1 }}
      label="Street Address"
      value={address || ""}
      disabled={disabled}
      onChange={(e) => onAddressChange(e.target.value)}
      fullWidth
      required
      {...streetTextFieldProps}
    />

    <TextField
      label="ZIP/Postal Code"
      value={zipCode || ""}
      disabled={disabled}
      onChange={onZipChange}
      fullWidth
      required
      {...zipTextFieldProps}
    />

    {loading ? (
      <Box display="flex" justifyContent="center" my={3}>
        <CircularProgress />
      </Box>
    ) : (
      <>
        <LabeledCreatableSelect
          label="City"
          options={allCities.map((c) => ({ label: c, value: c }))}
          value={city ? { label: city, value: city } : null}
          onChange={(opt) => onCityChange(opt ? opt.value : "")}
          placeholder="Select or add a city"
          disabled={disabled}
        />
        <LabeledCreatableSelect
          label="State"
          options={allStates.map((s) => ({ label: s, value: s }))}
          disabled={disabled}
          value={state ? { label: state, value: state } : null}
          onChange={(opt) => onStateChange(opt ? opt.value : "")}
          placeholder="Select or add a state"
        />
      </>
    )}
  </>
);

AddressFields.propTypes = {
  address: PropTypes.string,
  zipCode: PropTypes.string,
  city: PropTypes.string,
  state: PropTypes.string,
  onAddressChange: PropTypes.func.isRequired,
  onZipChange: PropTypes.func.isRequired,
  onCityChange: PropTypes.func.isRequired,
  onStateChange: PropTypes.func.isRequired,
  onGoogleCallback: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  allCities: PropTypes.array.isRequired,
  allStates: PropTypes.array.isRequired,
  GoogleAutoComplete: PropTypes.elementType.isRequired,
  LabeledCreatableSelect: PropTypes.elementType.isRequired,
  googlePlaceholder: PropTypes.string,
  streetTextFieldProps: PropTypes.object,
  zipTextFieldProps: PropTypes.object,
};
